/**
 * Portable API client for the R2YN shared backend.
 *
 * ✅ FIXED for Cloudflare CORS — uses credentials:'omit' and sanitized headers.
 *
 * This client speaks the same JWT + Bearer auth as the mobile app so both
 * clients share sessions and any key you add on one side is instantly
 * available on the other.
 */

// ─── Configuration ───────────────────────────────────────────────────────────
// CRA (Create React App) → process.env.REACT_APP_API_URL
// Vite → import.meta.env.VITE_API_URL
// Next.js → process.env.NEXT_PUBLIC_API_URL
const BASE_URL =
  process.env.REACT_APP_API_URL ||
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  '';

if (!BASE_URL) {
  console.warn(
    '[R2YN API] BASE_URL is empty. Set REACT_APP_API_URL in .env'
  );
}

// Check if shared backend is configured
export function isSharedBackendAvailable() {
  return !!BASE_URL;
}

// ─── Token storage ───────────────────────────────────────────────────────────
const TOKEN_KEY = 'r2yn_jwt';

export function setToken(token) {
  if (typeof window === 'undefined') return;
  if (token) window.localStorage.setItem(TOKEN_KEY, token);
  else window.localStorage.removeItem(TOKEN_KEY);
}

export function getToken() {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(TOKEN_KEY);
}

// ─── Core request helper ─────────────────────────────────────────────────────
// The Emergent Cloudflare ingress on `.emergent.host` publishes a fixed
// CORS allow-headers list. ANY header outside this list will cause the
// browser preflight OPTIONS to return HTTP 400, blocking the request with
// a misleading "Access-Control-Allow-Origin" error.
const CLOUDFLARE_SAFE_HEADERS = new Set([
  'accept',
  'accept-language',
  'authorization',
  'content-language',
  'content-type',
  'x-requested-with',
]);

function sanitizeHeaders(headers) {
  const out = {};
  const dropped = [];
  for (const [k, v] of Object.entries(headers)) {
    if (CLOUDFLARE_SAFE_HEADERS.has(k.toLowerCase())) {
      out[k] = v;
    } else {
      dropped.push(k);
    }
  }
  if (dropped.length) {
    console.warn(
      '[R2YN API] Dropped unsafe headers to keep CORS preflight passing:',
      dropped,
      '\n(Emergent Cloudflare only permits Accept, Accept-Language, Authorization, Content-Language, Content-Type, X-Requested-With.)'
    );
  }
  return out;
}

async function request(path, opts = {}) {
  const token = getToken();
  // ⚠️ IMPORTANT: only use headers on the Cloudflare/Emergent CORS
  // allowlist (Authorization, Content-Type, Accept, X-Requested-With,
  // Accept-Language, Content-Language). Custom headers like `X-Client-Name`
  // will cause the CORS preflight to fail with HTTP 400.
  const raw = {
    'Content-Type': 'application/json',
    ...(opts.headers || {}),
  };
  if (token) raw.Authorization = `Bearer ${token}`;
  const headers = sanitizeHeaders(raw);

  const res = await fetch(`${BASE_URL}/api${path}`, {
    ...opts,
    headers,
    // ✅ CRITICAL FIX: JWT is sent in the `Authorization` header — we do NOT need cookies.
    // `credentials: 'omit'` makes this SDK immune to the Cloudflare ingress
    // rewriting CORS headers to `Access-Control-Allow-Origin: *` on the
    // published URL (browsers accept `*` for non-credentialed requests).
    credentials: 'omit',
  });

  if (res.status === 401) {
    setToken(null);
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    throw new Error('Unauthorized');
  }
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      msg = body.detail || body.message || JSON.stringify(body);
    } catch {
      msg = await res.text() || msg;
    }
    throw new Error(msg);
  }
  if (res.status === 204) return undefined;
  return await res.json();
}

// ─── Health / info ───────────────────────────────────────────────────────────
export const meta = {
  info: () => request('/backend/info'),
  health: () => request('/health'),
};

// ─── Auth ────────────────────────────────────────────────────────────────────
export const auth = {
  async login(email, password) {
    const data = await request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setToken(data.access_token);
    return data;
  },
  logout() {
    setToken(null);
    if (typeof window !== 'undefined') {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    }
  },
  me: () => request('/auth/me'),
};

// ─── Admin: Integrations Vault ───────────────────────────────────────────────
export const adminIntegrations = {
  list: () => request('/admin/integrations'),
  
  get: (provider, mode = 'test') =>
    request(`/admin/integrations/${provider}?mode=${mode}`),
  
  update: (provider, mode, secret_updates, config_updates) =>
    request(`/admin/integrations/${provider}`, {
      method: 'PUT',
      body: JSON.stringify({ mode, secret_updates, config_updates }),
    }),
  
  toggle: (provider, mode, active) =>
    request(`/admin/integrations/${provider}/toggle`, {
      method: 'POST',
      body: JSON.stringify({ mode, active }),
    }),
  
  test: (provider, mode = 'test') =>
    request(`/admin/integrations/${provider}/test?mode=${mode}`, {
      method: 'POST',
    }),
  
  audit: (limit = 50) =>
    request(`/admin/integrations/audit?limit=${limit}`),
};

// ─── Admin: Users / Vendors / Drivers ───────────────────────────────────────
export const adminUsers = {
  list: (opts = {}) => {
    const q = new URLSearchParams();
    if (opts.role) q.set('role', opts.role);
    if (opts.limit) q.set('limit', String(opts.limit));
    if (opts.skip) q.set('skip', String(opts.skip));
    return request(`/admin/users?${q}`);
  },
  
  suspend: (id, reason) =>
    request(`/admin/users/${id}/suspend`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
    }),
  
  unsuspend: (id) =>
    request(`/admin/users/${id}/unsuspend`, { method: 'PUT' }),
};

export const adminVendors = {
  list: () => request('/admin/vendors'),
  
  approve: (id) =>
    request(`/admin/vendors/${id}/approve`, { method: 'POST' }),
  
  reject: (id, reason) =>
    request(`/admin/vendors/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
};

export const adminDrivers = {
  list: () => request('/admin/workers'),
  
  suspend: (id, reason) =>
    request(`/admin/workers/${id}/suspend`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
    }),
  
  unsuspend: (id) =>
    request(`/admin/workers/${id}/unsuspend`, { method: 'PUT' }),
};

// ─── Vendor: Dashboard / Menu / Orders ──────────────────────────────────────
export const vendor = {
  dashboard: () => request('/vendor/dashboard'),
  menu: () => request('/vendor/menu'),
  
  addMenuItem: (item) =>
    request('/vendor/menu', {
      method: 'POST',
      body: JSON.stringify(item),
    }),
  
  updateMenuItem: (id, patch) =>
    request(`/vendor/menu/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),
  
  orders: () => request('/vendor/orders'),
  
  acceptOrder: (id) =>
    request(`/vendor/orders/${id}/accept`, { method: 'POST' }),
  
  declineOrder: (id, reason) =>
    request(`/vendor/orders/${id}/decline`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  
  analytics: () => request('/vendor/analytics'),
  
  availability: (vendorId) =>
    request(`/vendors/${vendorId}/availability`),
  
  setAvailability: (vendorId, payload) =>
    request(`/vendors/${vendorId}/availability`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
};

// ─── Utility: One-line startup check ────────────────────────────────────────
export async function ensureSharedBackend() {
  const info = await meta.info();
  const expected = 'r2yn-shared-backend';
  if (info.backend_id !== expected) {
    throw new Error(
      `[R2YN API] Wrong backend: got ${info.backend_id}, expected ${expected}`
    );
  }
  console.log(
    '[R2YN API] ✓ Shared backend',
    info.version,
    Object.keys(info.features || {})
  );
}

export default {
  auth,
  meta,
  adminIntegrations,
  adminUsers,
  adminVendors,
  adminDrivers,
  vendor,
  setToken,
  getToken,
  isSharedBackendAvailable,
  ensureSharedBackend,
};
